/**
 * Workspace — Unified Studio + Designer
 * Bootstrap: init auth, parse URL params, detect mode, render shell
 */

// VI: fonts (Bricolage Grotesque + Rubik + Phosphor) + theme tokens are now
// sourced from @mamamiya/brand. bootTheme() is fire-and-forget — bundled
// default tokens render immediately, a live palette fetch overrides on resolve.
import '@mamamiya/brand/fonts'
import { bootTheme } from './theme-boot.js'
bootTheme()

/* global __BUILD_ID__ */
// Stamp the running build onto the pre-mount placeholder immediately — if the
// page ever sticks there, a screenshot tells us the JS ran AND which build it
// is (no stamp = the bundle never executed, i.e. stale HTML/proxy cache).
{
  const el = document.querySelector('#mode-create .pf-empty__msg')
  if (el) el.textContent = `Create Mode — AI Generation · ${__BUILD_ID__}`
}

import { store } from './core/store.js'
import { eventBus, EVENTS } from './core/event-bus.js'
import { CONFIG } from './core/config.js'
import { apiRequest } from './api/index.js'
import { parseCreateEntryParams } from './core/create-entry-params.js'
import { writeRefAttribution } from './utils/ref-attribution.js'
import { initRouter, setMode } from './core/router.js'
import { initSubtabs } from './core/subtabs.js'
import { applyPageFrame } from './modules/page-frame/page-frame.js'
import { initMobileNav } from './modules/mobile-nav/index.js'
import { initFirstRunGuide } from './modules/first-run-guide/first-run-guide.js' // H-136 A3 first-visit guide + Help replay
import {
  beginAuthAttempt,
  initAuth,
  initCanonicalAuthCallback,
  isAuthAttemptCurrent,
  saveAuth,
} from './modules/auth/index.js'
import { exchangeCreatorHubHandoffCode } from './modules/auth/canonical-client.js'
import { initLoginModal } from './modules/auth/login-modal.js'
import './utils/toast.js' // Self-initializing (listens to EVENTS.TOAST_SHOW)
import './client-handlers/handlers-init.js' // Self-registers F1-F7 handlers BEFORE create-mode init reads them
import './modules/create/index.js' // Self-registers create mode with router
import './modules/gallery/index.js' // Self-registers gallery mode with router
import { preloadDesigner } from './modules/design/index.js' // Self-registers design mode with router
import { applyCreateEntry } from './modules/studio/entry.js' // H-131 storefront create funnel
import './modules/products/index.js' // Self-registers products mode with router
import './modules/me/index.js' // Self-registers me mode with router
import { init as initFavorites } from './modules/me/favorites/index.js' // Favorites CRUD
import { initRightPanel } from './modules/create-ui/right-panel.js'
import { loadGallery } from './modules/gallery/index.js'
import { initCredits } from './modules/create-ui/credits-bar.js'
import { showPicGuardModal } from './modules/create-ui/picguard-modal.js'
import { showHistoryModal } from './modules/create-ui/history-modal.js'
import './modules/create-ui/queue.js' // Self-initializing (subscribes to store)

// Trusted parent origins for the AUTH postMessage handoff. Tightly scoped:
// only the mobile + maya surfaces ever wrap us in an iframe with a customer
// token to hand off. Anything else is ignored.
const TRUSTED_AUTH_ORIGINS = new Set([
  'https://mobile.mamamiya.store',
  'https://maya.mamamiya.store',
  // Shopify storefront — embeds creator-hub in the product-page modal
  // (mms-creator-modal.liquid) and hands off the customer token on iframe load.
  'https://mamamiya.store',
  'http://localhost:5174',
  'http://localhost:5175',
  'http://localhost:4175',
  'http://127.0.0.1:4175',
])

window.addEventListener('message', async (e) => {
  if (!TRUSTED_AUTH_ORIGINS.has(e.origin)) return
  const data = e.data
  // H-147: the storefront hints the logged-in Shopify customer's email so the
  // login modal can pre-fill it. UX convenience ONLY — not a credential, so
  // it is stashed in the store and never validated or trusted as identity.
  if (data?.type === 'SHOPIFY_CUSTOMER_HINT') {
    if (typeof data.email === 'string' && data.email.trim()) {
      store.setState('auth.emailHint', data.email.trim())
    }
    return
  }
  if (data?.type !== 'AUTH') return

  // H-180: the handoff carries a one-time code (<=10min TTL, single use),
  // never a raw access token. The code redeems for a full token pair —
  // the 30d refresh chain is what keeps embedded sessions alive past the
  // 24h access expiry that killed the old raw-token handoff (REQ-CH-43).
  const handoffCode = typeof data.handoff === 'string' ? data.handoff : null
  if (!handoffCode) return

  // Every auth source shares one generation, so stored/URL validations cannot
  // commit after a newer trusted handoff.
  const attempt = beginAuthAttempt()
  const result = await exchangeCreatorHubHandoffCode(handoffCode)
  if (!isAuthAttemptCurrent(attempt)) return
  if (result.ok) {
    saveAuth(result.customer, result.token)
  } else {
    console.warn('[auth] postMessage handoff code rejected')
  }
})

function showResumeEmptyState() {
  const designer = document.getElementById('designer-container')
  if (!designer) return
  designer.innerHTML =
    '<div class="design-loading"><i class="ph ph-file-x" aria-hidden="true"></i><div class="design-loading-text">This design is unavailable. Start a new design to continue.</div></div>'
}

async function init() {
  // /auth/callback: complete the bounded Google exchange, then CONTINUE
  // mounting — boot must never gate the app on the callback network (the old
  // early-return left the placeholder up forever after Google sign-in).
  await initCanonicalAuthCallback()
  // Parse URL params (extension compat: ?handoff, ?injectImage, ?embed, ?mode)
  const params = new URLSearchParams(window.location.search)
  const entry = parseCreateEntryParams(window.location)
  // H-180: cross-surface jumps carry a one-time handoff code instead of a
  // raw token (?handoff=<code>). The code redeems for an access+refresh
  // pair via the canonical realm, then is stripped from the URL.
  const handoffCode = params.get('handoff')
  let shouldInitStoredAuth = !handoffCode
  const injectImage = entry.injectImage
  const product = entry.product
  const designId = entry.designId
  // Accept both ?embed=1 (Shopify modal + MayaPIX iframe) and ?embed=true
  // (legacy mobile/maya), plus the in-iframe fallback.
  const embedParam = params.get('embed')
  const embedMode = embedParam === 'true' || embedParam === '1' || window.self !== window.top

  // Apply embed mode class
  if (embedMode) {
    document.body.classList.add('embed-mode')
  }

  // Handle the handoff code from a trusted surface jump (storefront,
  // extension). Redeem BEFORE persisting anything — the code is single-use,
  // so a rejected exchange must leave no partial session behind. The param
  // is stripped from the URL whether or not it redeemed so it never
  // lingers in history or referer headers.
  if (handoffCode) {
    const cleanUrl = new URL(window.location)
    cleanUrl.searchParams.delete('handoff')
    window.history.replaceState(null, '', cleanUrl)
    const attempt = beginAuthAttempt()
    const result = await exchangeCreatorHubHandoffCode(handoffCode)
    if (isAuthAttemptCurrent(attempt)) {
      if (result.ok) {
        saveAuth(result.customer, result.token)
      } else {
        shouldInitStoredAuth = true
        // eslint-disable-next-line no-console
        console.warn('[auth] URL handoff code rejected by /api/v1/auth/handoff-code/exchange — ignoring')
      }
    }
  }

  // Handle injected image (storefront → create funnel, ADR-005 2.7.0).
  // params.get() already URL-decodes once — a second decodeURIComponent both
  // double-decoded legit URLs and threw URIError on stray '%' sequences, which
  // killed the whole app boot. A malformed inject param must never break init().
  if (injectImage) {
    try {
      store.setState('sourceImage', {
        url: injectImage,
        source: 'extension',
        pageUrl: params.get('pageUrl') || '',
        pageTitle: params.get('pageTitle') || '',
      })
      // Default to Create mode when image is injected (unless ?mode overrides)
      if (!params.get('mode')) {
        store.setState('mode', 'create')
      }
      // Clean injectImage from URL
      const cleanUrl = new URL(window.location)
      cleanUrl.searchParams.delete('injectImage')
      cleanUrl.searchParams.delete('pageUrl')
      cleanUrl.searchParams.delete('pageTitle')
      window.history.replaceState(null, '', cleanUrl)
    } catch (err) {
      console.warn('[Workspace] injectImage ignored (malformed):', err?.message || err)
    }
  }

  if (product) {
    store.setState('design.currentProduct', { id: product })
  }

  // H-140 design entry (action-pipeline state contract): design_blank
  // preselects the blank via the same store path as the legacy ?product=
  // deep-link; design_material hangs as a pending entry that Design-mode
  // activate() applies to the Arts panel head (locate/preselect ONLY — never
  // auto-injects the canvas). ref lands in the mms_ref attribution cookie.
  // Params are stripped once read (mirrors the injectImage/source cleanup).
  if (entry.designBlank || entry.designMaterial || entry.ref) {
    if (entry.designBlank) {
      store.setState('design.currentProduct', { id: entry.designBlank })
    }
    if (entry.designMaterial) {
      store.setState('design.entryMaterial', { id: entry.designMaterial })
    }
    if (entry.ref) writeRefAttribution(entry.ref)
    const cleanUrl = new URL(window.location)
    cleanUrl.searchParams.delete('design_blank')
    cleanUrl.searchParams.delete('design_material')
    cleanUrl.searchParams.delete('ref')
    window.history.replaceState(null, '', cleanUrl)
  }

  // Auth must never block first paint: awaiting a session fetch here stalls
  // forever behind a hung network (proxy/VPN) with NO error event, leaving the
  // pre-mount placeholder up permanently (bao 2026-10-24 stuck page). Mount
  // first; auth settles in the background and store subscribers repaint. Only
  // the owner-scoped design resume lookup genuinely needs auth settled first.
  const authReady = shouldInitStoredAuth ? initAuth() : Promise.resolve()
  authReady.catch((err) => console.warn('[Workspace] Auth init failed:', err))
  if (designId) await authReady

  if (designId) {
    try {
      const resumed = await apiRequest(
        `${CONFIG.SHOPIFY_API}/api/customer/designs/${encodeURIComponent(designId)}`
      )
      if (resumed?.data?.product?.identity?.baseProductId) {
        store.setState('design.currentProduct', { id: resumed.data.product.identity.baseProductId })
        store.setState('design.resume', resumed.data)
        store.setState('mode', 'design')
      } else {
        showResumeEmptyState()
      }
    } catch {
      showResumeEmptyState()
    }
  }

  // Initialize login modal (subscribes to auth.showLoginModal flag)
  initLoginModal()

  // Demo helper: ?auth=open opens the login modal on load (preview convenience).
  if (params.get('auth') === 'open') {
    store.setState('auth.showLoginModal', true)
  }

  // Initialize favorites (loads saved favorites from server)
  initFavorites().catch((err) => console.warn('[Workspace] Favorites init failed:', err))

  // Initialize L2 subtabs BEFORE the router (H-135a): the deep-linked ?tab=
  // must be in the store before the first mode activate reads it, and the
  // MODE_ACTIVATED listener must exist to render the tab bar.
  initSubtabs()

  // Initialize router (binds sidebar clicks, parses ?mode= param)
  // H-140: the parser also INFERS a mode from entry params (design_material /
  // design_blank imply design) — honor it when no explicit ?mode= is present.
  if (!params.get('mode') && entry.mode) {
    store.setState('mode', entry.mode)
  }
  initRouter()

  // H-131 storefront create funnel: ?source={images.id} skips the hero act
  // straight into the gallery reveal; ?entry=sources opens the source picker.
  // H-167: ?source=<artwork url> (PDP "MAKE IT MINE" deep link) slots the
  // whitelisted artwork URL as the source; a rejected URL still enters so the
  // refusal toast fires. Must run AFTER initRouter — the studio shell + its
  // IMAGE_SELECT listener only exist once create mode has activated. Params
  // are stripped from the URL whether or not they resolve (mirrors the
  // injectImage cleanup).
  if (entry.source || entry.sourceUrl || entry.sourceRejected || entry.entry) {
    const cleanUrl = new URL(window.location)
    cleanUrl.searchParams.delete('source')
    cleanUrl.searchParams.delete('entry')
    window.history.replaceState(null, '', cleanUrl)
    applyCreateEntry(entry).catch((err) =>
      console.warn('[Workspace] create entry failed:', err?.message || err)
    )
  }

  // Preload gallery data (first page) so the slot-click pick-modal has the
  // library ready. The docked Gallery picker was removed (ADR-005 2.8.0 #1).
  loadGallery().catch((e) => console.warn('[Workspace] Gallery preload failed:', e))

  // Preload the SDS designer canvas (default product) in the background so
  // Design mode opens instantly — init starts at app boot, not on first click
  // (bao 2026-06-05). Fire-and-forget; failure falls back to lazy init.
  preloadDesigner().catch((e) => console.warn('[Workspace] Designer preload failed:', e))

  // Initialize right panel (community feed + image detail + gallery picker)
  initRightPanel()

  // Mobile bottom nav (mounts only at viewports < 768px; hidden on desktop)
  initMobileNav()

  // First-run guide (H-136 A3): auto-shows once for new visitors (gated by
  // the mamamiya_first_run_done marker); also wires the Help rail button as
  // the replay-guidance entry (re-openable from Help). Independent of the router — the overlay
  // floats above whatever mode booted.
  initFirstRunGuide()

  // Initialize credits bar (fetches balance, renders in stage header)
  initCredits().catch((err) => console.warn('[Workspace] Credits init failed:', err))

  // Wire stage-header action buttons (PicGuard + History)
  initStageActionButtons()

  // Keyboard shortcut: 1-5 for mode switching
  document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
    const modes = ['create', 'gallery', 'products', 'design', 'me']
    const idx = parseInt(e.key) - 1
    if (idx >= 0 && idx < modes.length) {
      setMode(modes[idx])
    }
    // Escape to close modal
    if (e.key === 'Escape') requestClose()
  })

  // Closing the workspace. When embedded (Shopify storefront popup, mobile/maya
  // iframe) the HOST owns the window chrome — so ask the PARENT to close. Hiding
  // our own .modal-shell here would only blank the iframe while the host's
  // <dialog> stays open: the "I closed create but a blank window is still there"
  // bug. Standalone (not embedded) there is nothing to close TO — hiding the
  // shell left a blank page with no recovery (2026-10-28 review), so close is
  // a no-op and the × button stays hidden.
  function requestClose() {
    if (embedMode) {
      try {
        window.parent.postMessage({ type: 'CLOSE' }, '*')
      } catch {
        /* no parent */
      }
    }
  }

  // Close button — embed only; standalone has nowhere to close to.
  const closeBtn = document.querySelector('.modal-close')
  if (closeBtn) {
    if (embedMode) closeBtn.addEventListener('click', requestClose)
    else closeBtn.hidden = true
  }
}

/**
 * Add PicGuard and History buttons to the stage header actions area
 */
function initStageActionButtons() {
  const actions = document.getElementById('stageActions')
  if (!actions) return

  // PicGuard button — create-scoped page action (H-130: the actions slot is
  // per-mode; applyPageFrame shows a [data-stage-mode] group only on its mode).
  const pgBtn = document.createElement('button')
  pgBtn.className = 'stage-action-btn'
  pgBtn.id = 'wsPicGuardBtn'
  pgBtn.dataset.stageMode = 'create'
  pgBtn.title = 'PicGuard Safety Check'
  pgBtn.setAttribute('aria-label', 'PicGuard Safety Check')
  pgBtn.innerHTML = '<i class="ph ph-shield-check" aria-hidden="true"></i>'
  pgBtn.addEventListener('click', () => {
    const ref = store.getState('sourceImage')
    const result = store.getState('creation.result')
    const imageUrl = result?.url || ref?.url
    if (imageUrl) {
      showPicGuardModal(imageUrl)
    } else {
      eventBus.emit(EVENTS.TOAST_SHOW, {
        message: 'Upload or generate an image first',
        type: 'info',
      })
    }
  })
  actions.appendChild(pgBtn)

  // History button
  const histBtn = document.createElement('button')
  histBtn.className = 'stage-action-btn'
  histBtn.id = 'wsHistoryBtn'
  histBtn.dataset.stageMode = 'create'
  histBtn.title = 'Generation History'
  histBtn.setAttribute('aria-label', 'Generation History')
  histBtn.innerHTML = '<i class="ph ph-clock-counter-clockwise" aria-hidden="true"></i>'
  histBtn.addEventListener('click', () => showHistoryModal())
  actions.appendChild(histBtn)

  // These buttons are appended after initRouter()'s first applyPageFrame —
  // re-scope visibility so they stay hidden when booting into another mode.
  applyPageFrame(store.getState('mode') || 'create')
}

// Boot
init().catch((err) => console.error('[Workspace] Init failed:', err))

// Expose for debugging
window.__workspace = { store, eventBus, EVENTS, CONFIG, showPicGuardModal, showHistoryModal }
